// src/features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/components/HistoricalBacktestChart.tsx
//
// High-performance historical backtest chart for the Multi-Asset cockpit. It
// renders the EXACT walk-forward Net Portfolio Value computed by the backend's
// C-speed Numba engine (the `equity_curve` returned by /builder/global/live),
// against two reference lines:
//
//   * Capital Invested   — the step baseline (initial capital + cash-flow
//     injections). Profit is the gap between the green area and this line.
//   * 4% Savings Account  — the "opportunity cost" line, compounded daily on
//     the fly so the user instantly sees how far the managed ML book has
//     pulled ahead of simply leaving the money in the bank.
//
// Pure presentation: it takes the raw curve and does light client-side
// enrichment (savings baseline) + downsampling so a 3-year daily series stays
// buttery smooth.

import { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { EquityCurvePoint } from '../api';

// Daily-compounded 4% p.a. baseline (252 trading days/year).
const DAILY_SAVINGS_RATE = 0.04 / 252;

const dateFormatter = (tick: string): string => {
    const date = new Date(tick);
    if (Number.isNaN(date.getTime())) return tick;
    return `${date.toLocaleString('default', { month: 'short' })} '${date
        .getFullYear()
        .toString()
        .slice(-2)}`;
};

const currencyFormatter = (value: number): string =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(value);

interface EnrichedPoint extends EquityCurvePoint {
    savings_baseline: number;
}

interface TooltipProps {
    active?: boolean;
    label?: string;
    payload?: { dataKey: string; value: number }[];
}

// Custom tooltip that surfaces the live profit vs. capital invested.
const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
    if (!active || !payload || payload.length < 2) return null;

    const invested = payload.find((p) => p.dataKey === 'total_invested')?.value ?? 0;
    const value = payload.find((p) => p.dataKey === 'portfolio_value')?.value ?? 0;
    const savings = payload.find((p) => p.dataKey === 'savings_baseline')?.value ?? 0;

    const profit = value - invested;
    const isProfit = profit >= 0;

    return (
        <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-2xl min-w-[240px] z-50">
            <p className="text-slate-400 text-xs font-bold mb-3 uppercase tracking-wider border-b border-slate-800 pb-2">
                {label
                    ? new Date(label).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                      })
                    : ''}
            </p>
            <div className="space-y-2">
                <div className="flex justify-between items-center gap-4">
                    <span className="text-emerald-400 text-xs font-bold">
                        ML Portfolio (Net)
                    </span>
                    <span className="text-white font-mono font-bold text-sm">
                        {currencyFormatter(value)}
                    </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-amber-500 text-xs font-semibold">
                        4% Bank Interest
                    </span>
                    <span className="text-amber-100 font-mono text-xs">
                        {currencyFormatter(savings)}
                    </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-slate-500 text-xs">Capital Invested</span>
                    <span className="text-slate-400 font-mono text-xs">
                        {currencyFormatter(invested)}
                    </span>
                </div>

                <div className="h-px w-full bg-slate-800 my-2" />

                <div className="flex justify-between items-center gap-4 bg-slate-950 p-2 rounded-lg border border-slate-800">
                    <span className="text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                        Total Profit
                    </span>
                    <span
                        className={`font-mono font-black text-sm ${
                            isProfit ? 'text-emerald-400' : 'text-red-400'
                        }`}
                    >
                        {isProfit ? '+' : ''}
                        {currencyFormatter(profit)}
                    </span>
                </div>
            </div>
        </div>
    );
};

interface HistoricalBacktestChartProps {
    data: EquityCurvePoint[];
}

export const HistoricalBacktestChart = ({ data }: HistoricalBacktestChartProps) => {
    // Enrich with the dynamic 4% savings-account opportunity-cost line.
    const enrichedData = useMemo<EnrichedPoint[]>(() => {
        if (!data.length) return [];
        let savings = data[0].total_invested;
        return data.map((point, index) => {
            if (index > 0) {
                savings *= 1 + DAILY_SAVINGS_RATE;
                if (point.cash_flows_injected > 0) {
                    savings += point.cash_flows_injected;
                }
            }
            return { ...point, savings_baseline: savings };
        });
    }, [data]);

    // Symmetric padding around the observed value band.
    const bounds = useMemo<[number, number]>(() => {
        if (!enrichedData.length) return [0, 1];
        const lows = enrichedData.map((d) => Math.min(d.portfolio_value, d.total_invested));
        const highs = enrichedData.map((d) =>
            Math.max(d.portfolio_value, d.savings_baseline),
        );
        const minVal = Math.min(...lows);
        const maxVal = Math.max(...highs);
        const padding = (maxVal - minVal) * 0.1 || maxVal * 0.05;
        return [Math.max(0, minVal - padding), maxVal + padding];
    }, [enrichedData]);

    // Downsample very long daily series to keep the render fast + crisp.
    const chartData = useMemo<EnrichedPoint[]>(() => {
        if (enrichedData.length <= 800) return enrichedData;
        const step = Math.ceil(enrichedData.length / 800);
        return enrichedData.filter(
            (_, i) => i % step === 0 || i === enrichedData.length - 1,
        );
    }, [enrichedData]);

    if (!enrichedData.length) {
        return (
            <p className="muted text-sm">
                Run the optimiser to render the historical backtest.
            </p>
        );
    }

    return (
        <div className="w-full h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 4, left: 10, bottom: 0 }}
                >
                    <defs>
                        <linearGradient id="maoBacktestValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#334155"
                        opacity={0.2}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={dateFormatter}
                        stroke="#64748b"
                        fontSize={11}
                        tickMargin={10}
                        minTickGap={40}
                        axisLine={false}
                        tickLine={false}
                    />
                    <YAxis
                        domain={bounds}
                        tickFormatter={(val: number) => `$${(val / 1000).toFixed(0)}k`}
                        stroke="#64748b"
                        fontSize={11}
                        axisLine={false}
                        tickLine={false}
                        width={60}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{
                            stroke: '#64748b',
                            strokeWidth: 1,
                            strokeDasharray: '4 4',
                        }}
                    />

                    {/* Capital invested: step baseline (initial + injections). */}
                    <Area
                        type="stepAfter"
                        dataKey="total_invested"
                        stroke="#64748b"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        fillOpacity={0}
                        isAnimationActive={false}
                    />

                    {/* Opportunity cost: 4% savings account. */}
                    <Line
                        type="monotone"
                        dataKey="savings_baseline"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="6 6"
                        isAnimationActive={false}
                    />

                    {/* Reality: net portfolio value. */}
                    <Area
                        type="monotone"
                        dataKey="portfolio_value"
                        stroke="#10b981"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#maoBacktestValue)"
                        isAnimationActive
                        animationDuration={1200}
                        animationEasing="ease-out"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default HistoricalBacktestChart;
